import { useState, useMemo, useContext, useCallback, useEffect } from 'react';

import AccountContext from 'contexts/AccountContext';
import ComparisonContext from 'contexts/ComparisonContext';
import DashboardGadgetContext from 'contexts/DashboardGadgetContext';
import DatasetDefinitionsContext from 'contexts/DatasetDefinitionsContext';
import BaseViewsContext from 'contexts/BaseViewsContext';
import EntityDefinitionsContext from 'contexts/EntityDefinitionsContext';
import GqlClientContext from 'contexts/GqlClientContext';
import MetricCacheContext from 'contexts/MetricCacheContext';
import WeekStartsOnOverrideContext from 'contexts/WeekStartsOnOverrideContext';
import { MatrixSortContext } from 'components/V5Gadget/Matrix/MatrixSortContext';
import getMatrixDefaultSort from 'components/V5Gadget/Matrix/getMatrixDefaultSort';
import metricTypeCheckers from 'types/metricTypeCheckers';
import isDefined from 'isDefined';
import useUsedMetrics from '../useUsedMetrics';
import useDateScope from '../useDateScope';
import useFilterInput from '../useFilterInput';
import useLockedDebouncedEffect from '../useLockedDebouncedEffect';
import useRegisterInsightsMatrixValues from '../useRegisterInsightsGadgetValues/useRegisterInsightsMatrixValues';
import useToMetricInput from 'hooks/useToMetricInput';
import useValueFormatters from '../useValueFormatters';

import getMetricMatrix from './getMetricMatrix';
import isDeltaCol from './format/isDeltaCol';
import useMatrixOverrides from './overrideGroups/useMatrixOverrides';

const useMetricMatrix = ({
  chartDef,
  skip2DRowDataFormatting,
  currentSortBy,
  isSecondaryRowsStacked,
}: {
  chartDef: V5ChartDefinition;
  skip2DRowDataFormatting?: boolean;
  currentSortBy?: string;
  // Stacked secondary rows (drawn by DataTable): also fetch and build each
  // dimension A value's own row (secondaryRowParents)
  isSecondaryRowsStacked?: boolean;
}) => {
  // Every series' metrics gate the fetch; the own query fetches its own
  const { directlyUsed } = useUsedMetrics(chartDef);
  const { isIdResolved } = useContext(MetricCacheContext);
  const { weekStartsOnOverride } = useContext(WeekStartsOnOverrideContext);
  const { unitsLocale, weekStartsOn } = useContext(AccountContext);
  const { baseViews } = useContext(BaseViewsContext);
  const { datasets } = useContext(DatasetDefinitionsContext);
  const { getEntityAndDefaultAppForField } = useContext(
    EntityDefinitionsContext,
  );
  const { filterInput } = useFilterInput({
    field: chartDef.dimensionA ? chartDef.dimensionA.field : undefined,
  });
  // Columns that ask their own question are fetched apart (see
  // MatrixOverrideGroup)
  const { queryChartDef, overrides } = useMatrixOverrides({
    chartDef,
    filterInput,
  });
  const { usedMetrics } = useUsedMetrics(queryChartDef);

  const { dashboardGadget } = useContext(DashboardGadgetContext);
  const { currentComparison } = useContext(ComparisonContext);
  const { client } = useContext(GqlClientContext);
  const { isSortAbsolute, sortField, sortDirection } =
    useContext(MatrixSortContext);
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [columnDefs, setColDefs] = useState<MatrixColDef[]>([]);
  const [rowData, setRowData] = useState<MatrixRowData[]>([]);
  const [comparisonRowData, setComparisonRowData] = useState<
    MatrixComparisonRowData[] | undefined
  >([]);
  const [rowsToDisplay, setRowsToDisplay] = useState<
    MatrixComparisonRowData[] | MatrixRowData[]
  >([]);
  const [totals, setTotals] = useState<MatrixRowData[]>([]);
  const [secondaryRowParents, setSecondaryRowParents] = useState<
    MatrixComparisonRowData[] | MatrixRowData[] | undefined
  >();

  const { formatMetric } = useValueFormatters();
  const toMetricInput = useToMetricInput();

  const currentDateScope = useDateScope({});
  const previousDateScope = useDateScope({
    comparison: currentComparison,
  });

  const compareSortType = useMemo(() => {
    const defaultSortArgs = getMatrixDefaultSort(chartDef);
    if (!defaultSortArgs) {
      return undefined;
    }
    const { defaultSortMode } = defaultSortArgs;
    return defaultSortMode;
  }, [chartDef]);

  const defaultCompareSort = useMemo(() => {
    const defaultSortArgs = getMatrixDefaultSort(chartDef);
    if (!defaultSortArgs) {
      return undefined;
    }
    const { defaultSortMode, defaultSortMetricId } = defaultSortArgs;
    if (isDeltaCol(defaultSortMode)) {
      return `${defaultSortMetricId}-delta`;
    } else {
      return `${defaultSortMetricId}-percentDelta`;
    }
  }, [chartDef]);

  // For 2D date matrices sort is applied client-side by ag-Grid and need not
  // round-trip to the server. Keeping these undefined prevents sort clicks from
  // triggering a re-fetch (and its loading spinner) for this vis type.
  const isTwoDimensionalDate =
    !!chartDef.dimensionB && chartDef.dimensionB.fieldType === 'date';
  const overrideSortFieldInput = isTwoDimensionalDate ? undefined : sortField;
  const overrideSortDirectionInput = isTwoDimensionalDate
    ? undefined
    : sortDirection;
  const isSortAbsoluteInput = isTwoDimensionalDate ? undefined : isSortAbsolute;

  // A compound metric's input metrics load on-demand, after the compound
  // definition itself. Firing before they resolve would send the full
  // expression with a partial `metrics` list (missing inputs are silently
  // dropped downstream) — mirror useV5ChartData and gate the matrix fetch until
  // every compound input reaches a terminal state (loaded or errored).
  const compoundInputsResolved = useMemo(
    () =>
      directlyUsed
        .filter(metricTypeCheckers.isCompoundMetric)
        .flatMap((cm) => cm.metricIds)
        .every((id) => isIdResolved(id)),
    [directlyUsed, isIdResolved],
  );

  const inputProps = useMemo(() => {
    if (usedMetrics.length === 0 || !compoundInputsResolved) {
      return undefined;
    }

    return {
      baseViews: Object.values(baseViews)
        .filter(isDefined)
        .filter((bv) =>
          usedMetrics
            .filter(metricTypeCheckers.isNormalMetric)
            .some((m) => m.dataType === bv.type),
        ),
      datasets,
      client,
      chartDef: queryChartDef,
      overrides,
      filterInput,
      currentDateScope,
      previousDateScope,
      toMetricInput,
      unitsLocale,
      usedMetrics,
      skip2DRowDataFormatting,
      comparison: currentComparison,
      isDashboardGadget: !!dashboardGadget,
      defaultCompareSort,
      compareSortType,
      currentSortBy,
      weekStartsOn: weekStartsOnOverride ? weekStartsOnOverride : weekStartsOn,
      formatMetric,
      getEntityAndDefaultAppForField,
      isSortAbsolute: isSortAbsoluteInput,
      overrideSortField: overrideSortFieldInput,
      overrideSortDirection: overrideSortDirectionInput,
      isSecondaryRowsStacked,
    };
  }, [
    baseViews,
    client,
    compareSortType,
    compoundInputsResolved,
    currentComparison,
    currentDateScope,
    currentSortBy,
    dashboardGadget,
    datasets,
    defaultCompareSort,
    filterInput,
    formatMetric,
    getEntityAndDefaultAppForField,
    isSecondaryRowsStacked,
    isSortAbsoluteInput,
    overrideSortDirectionInput,
    overrideSortFieldInput,
    overrides,
    previousDateScope,
    queryChartDef,
    skip2DRowDataFormatting,
    toMetricInput,
    unitsLocale,
    usedMetrics,
    weekStartsOn,
    weekStartsOnOverride,
  ]);

  // Set isLoading true as soon as a new fetch is about to be triggered.
  // This replaces the previous pattern of passing setIsLoading into the
  // async getMetricMatrix function and calling it imperatively there.
  useEffect(() => {
    if (inputProps !== undefined) {
      setIsLoading(true);
    }
  }, [inputProps]);

  const responseHandler = useCallback((response: any) => {
    setTotals(response.totals);
    setSecondaryRowParents(response.secondaryRowParents);
    setColDefs(response.colDefs);
    setRowData(response.rowData);
    // @ts-ignore
    setComparisonRowData(response.comparisonRowData);
    if (response.comparisonRowData) {
      setRowsToDisplay(response.comparisonRowData);
    } else {
      setRowsToDisplay(response.rowData);
    }
    setIsLoading(false);
    setIsInitialLoading(false);
  }, []);

  useLockedDebouncedEffect({
    args: inputProps,
    responseHandler,
    callback: getMetricMatrix,
  });

  // Publish the rendered grid to the AI Insights Chat when this gadget is on a
  // report canvas. A lazy getter — nothing is computed unless a question is sent —
  // and a no-op wherever there's no Insights view scope (Explore modal, gadget
  // previews, Storybook). This is the capture point for the gadget types that
  // don't route through useV5ChartData: matrix, stackedBar, stackedArea, heatmap,
  // sankey.
  useRegisterInsightsMatrixValues({
    chartDef,
    rows: rowsToDisplay,
    columnDefs,
    isLoading,
  });

  return {
    totals,
    chartDef,
    columnDefs,
    rowData,
    comparisonRowData,
    rowsToDisplay,
    secondaryRowParents,
    defaultCompareSort,
    compareSortType,
    isLoading,
    isInitialLoading,
  };
};

export default useMetricMatrix;
